import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed } from 'vue';
import store from '@/store';
import {
  ExportSdsesType,
  ExportSdsesCharacterCode,
  ExportOperations,
} from '@/composables/useSdsesExport';
import SelectInTextField from '@/components/SelectInTextField.vue';
import { commitAddNotification } from '@/store/main/mutations';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { getFullName } from '@/utils/string';
import SingleSelect from '@/views/pages/companyPeriodicExport/SingleSelect.vue';
import { defineMessages } from '@/lib/i18n';
import { isValidLength } from '@/lib/rule';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { readAssertedUserProfile } from '@/store/main/getters';
import {
  GetCompanyPeriodicExportSettingDetail,
  GetSendableCompanyUser,
} from '@/interfaces/api/general/company';
import Vue from 'vue/types/umd';
import { datePatterns, dayOfWeeks, frequencies } from './constants';
import { StateItem } from '@/interfaces/lib/model';
import { readUserLocale } from '@/store/main/getters';
import {
  EXPORT_FILE_FORMAT_CSV,
  EXPORT_FILE_FORMAT_EXCEL,
} from '@/lib/constant';
import { CreatedUser } from '@/interfaces/api/general/user';

export type ExportFrequency = (typeof frequencies)[number];
export type ExportDatePattern = (typeof datePatterns)[number];
export type ExportDayOfWeek = (typeof dayOfWeeks)[number];

// MEMO: 現時点で新規と編集に差がないため同じ型にしている
export type ExportSettingForm = {
  id?: number;
  name: string;
  description: string | undefined;
  exportType: ExportSdsesType;
  exportFileFormat: string;
  exportFormat: string;
  exportCharacterCode: string;
  exportTime: string; // 0:00~23:00
  exportFrequency: ExportFrequency; // 頻度
  exportDatePattern: ExportDatePattern | undefined; // 日付
  exportDayOfWeek: ExportDayOfWeek | undefined; // 曜日
  recipientCc: string[] | undefined;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'PeriodicExportFormDialog',
  props: {
    isOpen: { type: Boolean },
    exportSettingDetail: null,
    operations: null,
    sendableUsers: null
  },
  emits: ["create", "update", "onClose"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'create', form: ExportSettingForm): Promise<void>;
  (e: 'update', form: ExportSettingForm): Promise<void>;
  (e: 'onClose'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  isOpen: boolean;
  exportSettingDetail?: GetCompanyPeriodicExportSettingDetail;
  operations: ExportOperations;
  sendableUsers: GetSendableCompanyUser[];
};





const m = defineMessages({
  createTitle: '定期エクスポート設定の新規作成',
  requiredMessage: '{target}を指定してください',
  maxLengthWithItem: '{target}は{length}文字以内で入力してください',
  maxLimitMessageWithItem: '{target}は{count}件以内にしてください',
});
const $t = i18n.t.bind(i18n);
const userProfile = readAssertedUserProfile(store) as CreatedUser; // MEMO: getFullNameではdepartmentsを使わないのでLint用にキャストで対応する

const frequencyOptions = [
  { label: $t(sharedMessages.everyDay).toString(), value: 'daily' },
  { label: $t(sharedMessages.everyWeek).toString(), value: 'weekly' },
  { label: $t(sharedMessages.everyMonth).toString(), value: 'monthly' },
];

const dayOfWeekOptions = $t(sharedMessages.weekdayShortSundayToSaturday)
  .toString()
  .split(',')
  .map((weekdayShort, index) => ({
    label: weekdayShort,
    value: dayOfWeeks[index],
  }));

const datePatternOptions = $t(sharedMessages.exportDatePatternList)
  .toString()
  .split(',')
  .map((date, index) => ({
    label: date,
    value: datePatterns[index],
  }));
datePatternOptions.splice(datePatterns.length - 1, 1); // 末日はまだ使わないので削除

const MAX_NAME_LENGTH = 20;
const MAX_DESCRIPTION_LENGTH = 100;
const MAX_RECIPIENT_CC = 10;
const SELECTABLE_TIME = [...Array(24)].map((_, i) => ({
  text: `${i}:00`,
  value: `${i}:00`,
}));

const name = ref('');
const description = ref('');
const exportType = ref<ExportSdsesType>('normal');
const exportFileFormat = ref();
const exportFormat = ref();
const exportCharacterCode = ref('utf-8');
const exportTime = ref('0:00');
const exportFrequency = ref<ExportFrequency>(frequencies[0]);
const exportDayOfWeek = ref<ExportDayOfWeek>();
const exportDatePattern = ref<ExportDatePattern>();
const searchRecipientCc = ref<string>();
const recipientCc = ref<string[]>([]);
const selectableCharacterCode = ref(false);
const recipientCcOptions = ref<StateItem[]>([]);
const disabledRecipientCcValues = ref<string[]>([]);
const isSubmitting = ref<boolean>(false);
const formRef = ref<Vue | null>(null);

const { generateInactiveMessage, isFeatureActive, COMPANY_FEATURES } =
  useCompanyFeatureComposable();

const isRiskAssessmentEnabled = computed(() =>
  isFeatureActive(COMPANY_FEATURES.CHEMICAN_RISK_ASSESSMENT)
);

const resetForm = () => {
  name.value = '';
  description.value = '';
  exportType.value = 'normal';
  exportFileFormat.value = undefined;
  exportFormat.value = undefined;
  exportCharacterCode.value = 'utf-8';
  exportTime.value = '0:00';
  exportFrequency.value = frequencies[0];
  exportDayOfWeek.value = undefined;
  exportDatePattern.value = undefined;
  selectableCharacterCode.value = false;
  searchRecipientCc.value = undefined;
  recipientCc.value = [];
};

const convertForm = (data: GetCompanyPeriodicExportSettingDetail) => {
  name.value = data.name;
  description.value = data.description ?? '';
  exportType.value = data.export_type as ExportSdsesType;
  exportFileFormat.value = data.export_file_format;
  exportFormat.value = data.export_format;
  exportCharacterCode.value = data.export_character_code ?? 'shift-jis';
  exportTime.value = data.export_time;
  exportFrequency.value = data.export_frequency as ExportFrequency;
  exportDayOfWeek.value = data.export_day_of_week as
    | ExportDayOfWeek
    | undefined;
  exportDatePattern.value = data.export_date_pattern as
    | ExportDatePattern
    | undefined;
  selectableCharacterCode.value =
    exportType.value === 'risk_assessment'
      ? false
      : props.operations[exportType.value]?.find(
          (operation) => operation.value === exportFileFormat.value
        )?.selectable_character_code ?? false;
  recipientCc.value = data.cc_user_ids ?? [];
};

const validateForm = (): string | null => {
  if (!name.value) {
    return $t(m.requiredMessage, {
      target: $t(sharedMessages.name),
    }).toString();
  } else if (name.value.length > MAX_NAME_LENGTH) {
    return $t(m.maxLengthWithItem, {
      target: $t(sharedMessages.name),
      length: MAX_NAME_LENGTH,
    }).toString();
  }

  if (description.value && description.value.length > MAX_DESCRIPTION_LENGTH) {
    return $t(m.maxLengthWithItem, {
      target: $t(sharedMessages.description),
      length: MAX_DESCRIPTION_LENGTH,
    }).toString();
  }

  if (!exportType.value) {
    return $t(m.requiredMessage, {
      target: $t(sharedMessages.exportType),
    }).toString();
  }

  if (exportType.value === 'normal') {
    if (!exportFileFormat.value) {
      return $t(m.requiredMessage, {
        target: $t(sharedMessages.fileFormat),
      }).toString();
    }
    if (
      exportFileFormat.value === EXPORT_FILE_FORMAT_CSV &&
      !exportFormat.value
    ) {
      return $t(m.requiredMessage, {
        target: $t(sharedMessages.exportFormat),
      }).toString();
    }
    if (
      exportFileFormat.value !== EXPORT_FILE_FORMAT_EXCEL &&
      !exportCharacterCode.value
    ) {
      return $t(m.requiredMessage, {
        target: $t(sharedMessages.exportCharacterCode),
      }).toString();
    }
  } else if (exportType.value === 'custom') {
    if (!exportFileFormat.value) {
      return $t(m.requiredMessage, {
        target: $t(sharedMessages.fileFormat),
      }).toString();
    }
    if (!exportFormat.value) {
      return $t(m.requiredMessage, {
        target: $t(sharedMessages.exportFormat),
      }).toString();
    }
    if (!exportCharacterCode.value) {
      return $t(m.requiredMessage, {
        target: $t(sharedMessages.exportCharacterCode),
      }).toString();
    }
  }

  if (recipientCc.value.length > MAX_RECIPIENT_CC) {
    return $t(m.maxLimitMessageWithItem, {
      target: $t(sharedMessages.recipientCc),
      count: MAX_RECIPIENT_CC,
    }).toString();
  }

  return null;
};

const disableItemHandler = (item: StateItem): boolean => {
  // MEMO: 現状これが呼び出し元で動作している気配はない
  return disabledRecipientCcValues.value.find((v) => v === item.value)
    ? true
    : false;
};

const handleChangeFrequency = (value: string) => {
  // 頻度の変更ごとに初期値に戻す
  switch (value) {
    case 'daily':
      exportDayOfWeek.value = undefined;
      exportDatePattern.value = undefined;
      break;
    case 'weekly':
      exportDayOfWeek.value = 'sunday';
      exportDatePattern.value = undefined;
      break;
    case 'monthly':
      exportDayOfWeek.value = undefined;
      exportDatePattern.value = '1';
      break;
    default:
      break;
  }
  exportFrequency.value = value as ExportFrequency;
};

const handleChangeDayOfWeek = (value: string) => {
  exportDayOfWeek.value = value as ExportDayOfWeek;
};

const handleChangeDayPattern = (value: string) => {
  exportDatePattern.value = value as ExportDatePattern;
};

const handleCloseDialog = () => {
  emits('onClose');
  resetForm();
};

const handleChangeExportType = (value: ExportSdsesType): void => {
  if (exportType.value !== value) {
    // エクスポート種別を変更した場合はファイル形式、エクスポート形式を選択し直しにする
    exportFileFormat.value = undefined;
    exportFormat.value = undefined;
  }
  exportType.value = value;
};

const handleChangeExportFileFormat = (value: string): void => {
  if (exportFileFormat.value !== value) {
    // ファイル形式を変更した場合は、エクスポート形式を選択し直しにする
    exportFormat.value = undefined;
  }
  exportFileFormat.value = value;
  selectableCharacterCode.value =
    exportType.value === 'risk_assessment'
      ? false
      : props.operations[exportType.value]?.find(
          (operation) => operation.value === exportFileFormat.value
        )?.selectable_character_code ?? false;
};

const handleChangeExportFormat = (value: string): void => {
  exportFormat.value = value;
};

const handleChangeExportCharacterCode = (
  value: ExportSdsesCharacterCode
): void => {
  exportCharacterCode.value = value;
};

const handleChangeRecipientCc = (values: string[] | number[]) => {
  recipientCc.value = values as string[];
};

const handleSubmit = async () => {
  const errorMessage = validateForm();

  if (errorMessage) {
    commitAddNotification(store, {
      messageKey: errorMessage,
      color: 'error',
    });
    return;
  }

  isSubmitting.value = true;
  if (props.exportSettingDetail) {
    // 更新
    const form: ExportSettingForm = {
      id: props.exportSettingDetail.id,
      name: name.value,
      description: description.value,
      exportType: exportType.value,
      exportFileFormat: exportFileFormat.value,
      exportFormat: exportFormat.value,
      exportCharacterCode: exportCharacterCode.value,
      exportTime: exportTime.value,
      exportFrequency: exportFrequency.value,
      exportDatePattern: exportDatePattern.value,
      exportDayOfWeek: exportDayOfWeek.value,
      recipientCc: recipientCc.value,
    };
    emits('update', form);
  } else {
    // 登録
    const form: ExportSettingForm = {
      name: name.value,
      description: description.value,
      exportType: exportType.value,
      exportFileFormat: exportFileFormat.value,
      exportFormat: exportFormat.value,
      exportCharacterCode: exportCharacterCode.value,
      exportTime: exportTime.value,
      exportFrequency: exportFrequency.value,
      exportDatePattern: exportDatePattern.value,
      exportDayOfWeek: exportDayOfWeek.value,
      recipientCc: recipientCc.value,
    };
    emits('create', form);
  }
  isSubmitting.value = false;
  handleCloseDialog();
};

watch(
  () => props.isOpen,
  () => {
    formRef.value?.$el.scrollIntoView(true);
    recipientCcOptions.value = props.sendableUsers.map((user) => ({
      value: user.user_id,
      title: user.user_name,
      disabled: user.disabled,
    }));
    disabledRecipientCcValues.value = props.sendableUsers
      .filter((user) => user.disabled)
      .map((user) => user.user_id);
  }
);
watch(
  () => props.exportSettingDetail,
  (newValue, oldValue) => {
    if (!newValue) {
      resetForm();
    } else if (newValue !== oldValue) {
      convertForm(newValue);
    }
  }
);

const isJapanese = computed(() => {
  const locale = readUserLocale(store)?.toLowerCase().trim();
  return (
    locale === 'ja' || locale.startsWith('ja-') || locale.startsWith('ja_')
  );
});

return { __sfc: true,props, emits, m, $t, userProfile, frequencyOptions, dayOfWeekOptions, datePatternOptions, MAX_NAME_LENGTH, MAX_DESCRIPTION_LENGTH, MAX_RECIPIENT_CC, SELECTABLE_TIME, name, description, exportType, exportFileFormat, exportFormat, exportCharacterCode, exportTime, exportFrequency, exportDayOfWeek, exportDatePattern, searchRecipientCc, recipientCc, selectableCharacterCode, recipientCcOptions, disabledRecipientCcValues, isSubmitting, formRef, generateInactiveMessage, isFeatureActive, COMPANY_FEATURES, isRiskAssessmentEnabled, resetForm, convertForm, validateForm, disableItemHandler, handleChangeFrequency, handleChangeDayOfWeek, handleChangeDayPattern, handleCloseDialog, handleChangeExportType, handleChangeExportFileFormat, handleChangeExportFormat, handleChangeExportCharacterCode, handleChangeRecipientCc, handleSubmit, isJapanese, SelectInTextField, getFullName, SingleSelect, isValidLength, sharedMessages }
}

})